import { Document, Paragraph, TextRun, HeadingLevel, AlignmentType, Table, TableRow, TableCell, WidthType, BorderStyle, Packer } from 'docx';
import { saveAs } from 'file-saver';
import type { StudyPlan } from '@/types';

export async function generateStudyPlanWord(studyPlan: StudyPlan): Promise<void> {
  const doc = new Document({
    sections: [{
      properties: {},
      children: [
        // Header - Utkast til (ikke godkjent)
        new Paragraph({
          text: `Utkast til (ikke godkjent): ${studyPlan.title}`,
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 },
        }),

        // Status info
        new Paragraph({
          children: [
            new TextRun({
              text: 'Status: ',
              bold: true,
            }),
            new TextRun({
              text: getStatusText(studyPlan.status),
            }),
          ],
          spacing: { after: 200 },
        }),

        // Metadata section
        new Paragraph({
          text: 'Informasjon',
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 300, after: 200 },
        }),

        // Metadata table
        createMetadataTable(studyPlan),

        // Description
        new Paragraph({
          text: 'Beskrivelse',
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 200 },
        }),
        new Paragraph({
          text: studyPlan.description,
          spacing: { after: 300 },
        }),

        // Kategorier (kompetanseheving.no)
        ...(Array.isArray(studyPlan.categories) && studyPlan.categories.length > 0 ? [
          new Paragraph({
            text: 'Kategorier (kompetanseheving.no)',
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400, after: 200 },
          }),
          new Paragraph({
            children: [
              new TextRun({ text: 'Primær: ', bold: true }),
              new TextRun({ text: studyPlan.categories[0] }),
            ],
            spacing: { after: 100 },
          }),
          ...(studyPlan.categories.length > 1 ? [
            new Paragraph({
              children: [
                new TextRun({ text: 'Øvrige: ', bold: true }),
                new TextRun({ text: studyPlan.categories.slice(1).join(', ') }),
              ],
              spacing: { after: 300 },
            }),
          ] : [new Paragraph({ text: '', spacing: { after: 200 } })]),
        ] : []),

        // Learning Outcomes
        new Paragraph({
          text: 'Læringsutbytte',
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 200 },
        }),

        // Knowledge
        ...(studyPlan.learning_outcomes.knowledge && studyPlan.learning_outcomes.knowledge.length > 0 ? [
          new Paragraph({
            text: 'Kunnskaper',
            heading: HeadingLevel.HEADING_3,
            spacing: { before: 200, after: 100 },
          }),
          ...studyPlan.learning_outcomes.knowledge.map(item =>
            new Paragraph({
              text: `• ${item}`,
              spacing: { after: 100 },
            })
          ),
        ] : []),

        // Skills
        ...(studyPlan.learning_outcomes.skills && studyPlan.learning_outcomes.skills.length > 0 ? [
          new Paragraph({
            text: 'Ferdigheter',
            heading: HeadingLevel.HEADING_3,
            spacing: { before: 200, after: 100 },
          }),
          ...studyPlan.learning_outcomes.skills.map(item =>
            new Paragraph({
              text: `• ${item}`,
              spacing: { after: 100 },
            })
          ),
        ] : []),

        // Competence
        ...(studyPlan.learning_outcomes.competence && studyPlan.learning_outcomes.competence.length > 0 ? [
          new Paragraph({
            text: 'Kompetanse',
            heading: HeadingLevel.HEADING_3,
            spacing: { before: 200, after: 100 },
          }),
          ...studyPlan.learning_outcomes.competence.map(item =>
            new Paragraph({
              text: `• ${item}`,
              spacing: { after: 100 },
            })
          ),
        ] : []),

        // Content Modules
        ...(studyPlan.content_modules && studyPlan.content_modules.length > 0 ? [
          new Paragraph({
            text: 'Innholdsmoduler',
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400, after: 200 },
          }),
          ...studyPlan.content_modules.map(module =>
            new Paragraph({
              text: `• ${module}`,
              spacing: { after: 100 },
            })
          ),
        ] : []),

        // Assessment Criteria
        new Paragraph({
          text: 'Vurderingskriterier',
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 200 },
        }),
        new Paragraph({
          text: studyPlan.assessment_criteria,
          spacing: { after: 300 },
        }),

        // Assessment Methods
        ...(studyPlan.metadata.assessment_methods && studyPlan.metadata.assessment_methods.length > 0 ? [
          new Paragraph({
            text: 'Vurderingsmetoder',
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400, after: 200 },
          }),
          ...studyPlan.metadata.assessment_methods.map(method =>
            new Paragraph({
              text: `• ${method}`,
              spacing: { after: 100 },
            })
          ),
        ] : []),

        // Validation Results if available
        ...(studyPlan.validation_feedback && studyPlan.validation_score ? [
          new Paragraph({
            text: 'Valideringsresultat',
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 400, after: 200 },
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: 'Totalscore: ',
                bold: true,
              }),
              new TextRun({
                text: `${Number(studyPlan.validation_score).toFixed(1)}%`,
              }),
            ],
            spacing: { after: 200 },
          }),
          new Paragraph({
            text: studyPlan.validation_feedback.summary || '',
            spacing: { after: 300 },
          }),
        ] : []),

        // Footer
        new Paragraph({
          children: [
            new TextRun({
              text: `Generert: ${new Date().toLocaleString('no-NO')}`,
              italics: true,
            }),
          ],
          alignment: AlignmentType.CENTER,
          spacing: { before: 600 },
        }),
      ],
    }],
  });

  // Generate and download
  const blob = await Packer.toBlob(doc);
  const fileName = `${studyPlan.title.replace(/[^a-zA-Z0-9æøåÆØÅ\s]/g, '_')}_utkast.docx`;
  saveAs(blob, fileName);
}

function getStatusText(status: string): string {
  const statusMap: Record<string, string> = {
    'draft': 'Utkast',
    'submitted': 'Innsendt',
    'under_review': 'Under vurdering',
    'approved': 'Godkjent',
    'rejected': 'Avvist',
  };
  return statusMap[status] || status;
}

function createMetadataTable(studyPlan: StudyPlan): Table {
  return new Table({
    width: {
      size: 100,
      type: WidthType.PERCENTAGE,
    },
    borders: {
      top: { style: BorderStyle.SINGLE, size: 1 },
      bottom: { style: BorderStyle.SINGLE, size: 1 },
      left: { style: BorderStyle.SINGLE, size: 1 },
      right: { style: BorderStyle.SINGLE, size: 1 },
      insideHorizontal: { style: BorderStyle.SINGLE, size: 1 },
      insideVertical: { style: BorderStyle.SINGLE, size: 1 },
    },
    rows: [
      new TableRow({
        children: [
          new TableCell({
            children: [new Paragraph({
              children: [new TextRun({ text: 'Kompetansepoeng', bold: true })],
            })],
            width: { size: 40, type: WidthType.PERCENTAGE },
          }),
          new TableCell({
            children: [new Paragraph({ text: studyPlan.ects_credits.toString() })],
            width: { size: 60, type: WidthType.PERCENTAGE },
          }),
        ],
      }),
      new TableRow({
        children: [
          new TableCell({
            children: [new Paragraph({
              children: [new TextRun({ text: 'Normativ studiebelasting', bold: true })],
            })],
          }),
          new TableCell({
            children: [new Paragraph({ text: `${studyPlan.estimated_hours} timer` })],
          }),
        ],
      }),
      new TableRow({
        children: [
          new TableCell({
            children: [new Paragraph({
              children: [new TextRun({ text: 'EQF-nivå', bold: true })],
            })],
          }),
          new TableCell({
            children: [new Paragraph({ text: `Nivå ${studyPlan.metadata.eqf_level || 'N/A'}` })],
          }),
        ],
      }),
      new TableRow({
        children: [
          new TableCell({
            children: [new Paragraph({
              children: [new TextRun({ text: 'Studieforbund', bold: true })],
            })],
          }),
          new TableCell({
            children: [new Paragraph({ text: (studyPlan as any).study_association_name || 'Ukjent' })],
          }),
        ],
      }),
    ],
  });
}
